/**
 * SignInGate — full-screen overlay shown when the Stream operator does not
 * have a valid `@kodeon.ai` Google session (task 3127).
 *
 * Sits ahead of the OperatorSelector in the boot sequence: rendered whenever
 * `useGoogleAuthStore.status` is `'unauthenticated'` or `'signing_in'`.
 * On a fresh machine the operator clicks "Sign in with Google" — the Rust
 * layer opens the system browser, waits for the OAuth callback, verifies the
 * `hd` claim against `kodeon.ai`, and stores the refresh token in the
 * macOS Keychain. On subsequent launches the silent refresh runs through
 * before this component would have a chance to render.
 *
 * Styling follows the Stream convention: CSS-in-JS with design tokens from
 * `src/styles/global.css`. See ARCHITECTURE.md and STYLING.md.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { IS_TAURI } from '../lib/staff-session'
import { cancelPrompt, renderSignInButton } from '../lib/gis'
import streamIcon from '@/assets/stream-icon.jpg'
import { useGoogleAuthStore } from '../stores/google-auth-store'

export function SignInGate() {
  const status = useGoogleAuthStore((s) => s.status)
  const error = useGoogleAuthStore((s) => s.error)
  const startSignIn = useGoogleAuthStore((s) => s.startSignIn)
  const completeBrowserSignIn = useGoogleAuthStore((s) => s.completeBrowserSignIn)
  const isSigningIn = status === 'signing_in'
  const googleButtonRef = useRef<HTMLDivElement | null>(null)
  // Bumped to re-render Google's button if this gate's waiter is ever
  // displaced. setWaiter now refuses to let a background refresh displace an
  // interactive waiter, so this is defence in depth rather than the primary
  // fix — but a gate that cannot re-arm is how B3 became permanent.
  const [armGeneration, setArmGeneration] = useState(0)

  // Browser only: mount Google's rendered button and hand the resulting
  // credential to the store.
  useEffect(() => {
    if (IS_TAURI || !googleButtonRef.current) return
    let cancelled = false

    void renderSignInButton(googleButtonRef.current)
      .then(async (idToken) => {
        if (cancelled) return
        await completeBrowserSignIn(idToken)
        // Re-arm if sign-in did not stick (PR #111 N-new-1). settleWaiter nulls
        // the waiter when the credential resolves, so after a FAILED profile
        // lookup — `staff_not_found`, or a transient blip — the gate stays
        // mounted with a button that looks clickable but has no waiter behind
        // it. The operator gets "ask an admin to create your record", the admin
        // creates it, and clicking again does nothing forever.
        if (
          !cancelled &&
          useGoogleAuthStore.getState().status !== 'authenticated'
        ) {
          setArmGeneration((n) => n + 1)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err)
        // A user closing the flow is not worth surfacing. But two genuine hard
        // failures reach here (PR #111 N9), and both leave an EMPTY 44px gap
        // where the button should be, with no error and no way forward:
        // a missing VITE_GOOGLE_OAUTH_CLIENT_ID (a misconfigured deploy — every
        // operator gets a silent blank gate), and a blocked GIS CDN (corporate
        // proxy or extension). Both are build/environment problems that
        // retrying never fixes, so they must be self-diagnosing.
        // `Superseded` is NOT a user cancel (PR #111 B3) — it means something
        // displaced this waiter, which the operator did not do and cannot see.
        // Silently ignoring it is what made the dead-button case invisible.
        const isUserCancel = message.includes('dismissed')
        if (message.includes('Superseded')) {
          // Reinstall the button rather than leaving a dead one on screen.
          setArmGeneration((n) => n + 1)
          return
        }
        if (!isUserCancel) {
          useGoogleAuthStore.setState({ error: message })
        }
      })

    return () => {
      cancelled = true
      // Drop the rendered iframe and any prompt this gate put on screen.
      void cancelPrompt()
      if (googleButtonRef.current) googleButtonRef.current.innerHTML = ''
    }
  }, [completeBrowserSignIn, armGeneration])

  return (
    <div
      style={styles.container}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sign-in-gate-heading"
      data-testid="sign-in-gate"
    >
      <div style={styles.panel}>
        <div style={styles.logoWrap}>
          <img src={streamIcon} alt="" style={styles.logo} />
        </div>
        <p style={styles.eyebrow}>Kodeon Stream</p>
        <h1 id="sign-in-gate-heading" style={styles.heading}>
          Sign in to continue
        </h1>
        <p style={styles.copy}>
          Stream is restricted to <strong>@kodeon.ai</strong> Google Workspace
          accounts. Sign in with your Kodeon Google account to unlock the app.
        </p>

        {IS_TAURI ? (
          <button
            type="button"
            onClick={() => void startSignIn()}
            disabled={isSigningIn}
            style={{
              ...styles.button,
              opacity: isSigningIn ? 0.6 : 1,
              cursor: isSigningIn ? 'progress' : 'pointer',
            }}
            className="button-primary"
            data-testid="sign-in-gate-button"
          >
            {isSigningIn ? 'Waiting for browser…' : 'Sign in with Google'}
          </button>
        ) : (
          // Browser: Google's own rendered button, not ours.
          //
          // One Tap's prompt() cannot be driven from a click — it is designed
          // to appear unprompted, and once dismissed it returns
          // `unknown_reason` for the rest of the session with no way to force
          // it. renderButton is the supported user-initiated path. The cost is
          // that this element is Google's, so it cannot take Stream's styling.
          <div ref={googleButtonRef} style={styles.googleButtonSlot} />
        )}

        {isSigningIn && (
          <p style={styles.hint}>
            Complete sign-in in your browser, then return here. This window
            will unlock automatically.
          </p>
        )}

        {error && !isSigningIn && (
          <p style={styles.error} role="alert" data-testid="sign-in-gate-error">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    zIndex: 9000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    background: 'transparent',
  },
  panel: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    maxWidth: '440px',
    padding: '40px 32px',
    textAlign: 'center',
    // Theme-aware tokens, NOT the `tokens.*` module — that exports static
    // dark-mode hexes and is not theme-reactive. A white-alpha overlay only
    // reads as a surface over a dark ground; over light it vanished and the
    // card lost its edge.
    background: 'var(--surface-card-soft)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    backdropFilter: 'blur(12px)',
    boxShadow: 'var(--shadow-lg)',
  },
  logoWrap: {
    display: 'flex',
    width: '72px',
    height: '72px',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '4px',
    borderRadius: '18px',
    // The accent ring is hue-neutral, so the accent tokens hold in both
    // themes; --logo-ring-shadow carries the glow and drops the white bevel
    // under light.
    border: '1px solid var(--accent-border-strong)',
    background: 'var(--accent-bg-medium)',
    boxShadow: 'var(--logo-ring-shadow)',
    overflow: 'hidden',
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  eyebrow: {
    margin: 0,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
  },
  heading: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
  },
  copy: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.55,
    color: 'var(--text-secondary)',
  },
  googleButtonSlot: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: '44px',
  },
  button: {
    marginTop: '8px',
    minWidth: '220px',
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    borderRadius: 'var(--radius-md)',
    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
    transition: 'all var(--transition-fast)',
  },
  hint: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  error: {
    margin: 0,
    maxWidth: '360px',
    padding: '8px 12px',
    fontSize: '12px',
    color: 'var(--error)',
    background: 'var(--error-bg-medium)',
    border: '1px solid var(--error-border-medium)',
    borderRadius: 'var(--radius-sm)',
    wordBreak: 'break-word',
  },
}
